import { yupResolver } from '@hookform/resolvers/yup';
import * as React from 'react';
import { Path } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { UseCreateStoreFormInterface } from '@/model/profile.mjs';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Input,
  ModalBody,
  ModalFooter,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Text,
  ChakraProvider
} from '@chakra-ui/react';
import { createStoreSchema } from '@/model/yupSchema.mjs';
import TableElement from '@/table/TableElement';
import { InputField } from '@/profile/input';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import StoreDetailsInterface from '@/model/store.mjs';
import { ProfileIntialValue } from '@/profile/profile';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import { LicenseById, uploadCertificateInS3 } from '@/api/apis';
import Licensee from '@/model/licensee.mjs';
import { callToGetCreateStoreApi, setCreateStoreData, storeDataInitialValue } from '@/redux/app-slice';
import { formatPhoneNumber, getPrimaryValue } from '@/explore/ffl-detail/utils.mjs';
import { ToastContainer } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { getCreateStoreData } from '@/redux/app-selectors';
import { BUSINESS_NAME } from '@/utils/constants';

interface onCloseModelInterface {
  onClosePopUp:()=>void
}

export const CreateStoreForm = ({onClosePopUp}:onCloseModelInterface) => {
  const createStoreData = getCreateStoreData();
  const [licenseKey, setLicenseKey] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreateLoading, setIsCreateLoading] = useState<boolean>(false);
  const [profileData, setProfileData] =
    useState<StoreDetailsInterface>(ProfileIntialValue);
  const [islicenseValidated, setIsLicenseValidated] = useState<boolean>(false);
  const [file, setFile] = useState<FileList | null>();
  const dispatch = useDispatch();
  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createStoreSchema),
    mode: 'onChange',
    values: {
      store_license_key: profileData.store_license_key || '',
      contact_email: profileData.contact_email || '',
      store_premise_city: profileData.store_premise_city || '',
      store_premise_state: profileData.store_premise_state || '',
      store_premise_street: profileData.store_premise_street || '',
      store_premise_zip_code: profileData.store_premise_zip_code || '',
      store_contact_fax: profileData.store_contact_fax || '',
      seller_email: profileData.seller_email || ''
    }
  });
  React.useEffect(() => {
    if(createStoreData?.store_id){
      setIsCreateLoading(false);
      onClosePopUp();
      dispatch(setCreateStoreData(storeDataInitialValue))
    }
  }, [createStoreData])
  
  async function createHandler() {
    try{
      setIsCreateLoading(true);
      let formData = {
        ...profileData,
        store_id: uuidv4()
      };
      if (file && file[0]?.name) {
        formData = {
          ...formData,
        store_license_file_path: `certificates/${formData.store_id}/${file[0]?.name}`
      };
      uploadCertificateInS3(file[0], formData.store_id)
      .then((res) => console.log('Uploaded: ', res))
      .catch((e) => console.log('Upload Error: ', String(e)));
    }
    dispatch(callToGetCreateStoreApi(formData));
  }catch(error){
    setIsCreateLoading(false);
  }
  }

  function renderInputField(
    data: string,
    name: Path<UseCreateStoreFormInterface>
  ) {
    if (name === 'store_license_key') return licenseInput();
    return (
      <InputField
        detail={data}
        name={name}
        register={register}
        setProfileData={setProfileData}
        profileData={profileData}
        isLicenseValidated={islicenseValidated}
      />
    );
  }
  function getLicenseData() {
    LicenseById(
      licenseKey.substring(0, 3),
      licenseKey.substring(licenseKey.length - 5)
    )
      .then((data: Licensee) => {
        setIsLoading(false);
        setIsLicenseValidated(true);
        const store_lic_type = data.licenseId?.split('-')[3] || '';
        setProfileData({
          ...profileData,
          contact_phone:
          data?.contact?.formattedPhoneNumbers ? getPrimaryValue(data?.contact?.formattedPhoneNumbers) : '',
          name: data?.name || '',
          contact_email: data.contact.email?.toString() || '',
          store_premise_city: data.contact.address?.city || '',
          store_premise_state: data.contact.address?.state || '',
          store_premise_street: data.contact.address?.street1 || '',
          store_premise_zip_code: data.contact.address?.zip || '',
          store_contact_fax: '',
          store_lic_type,
          store_license_key: licenseKey
        });
      })
      .catch((e: any) => {
        console.log(String(e));
        setIsLicenseValidated(false);
      });
  }

  function licenseInput() {
    const regex = new RegExp(/^[0-9]{8}$/g);
    return (
      <>
        <Grid templateColumns={'repeat(8, 1fr)'}>
          <GridItem colSpan={7}>
            <Input
              isDisabled={isLoading}
              type="text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLicenseKey(e.target.value)
              }
            />
          </GridItem>
          <GridItem>
            <Button
              isDisabled={isLoading || !regex.test(licenseKey)}
              onClick={() => {
                setIsLoading(true);
                getLicenseData();
              }}
            >
              {isLoading ? <Spinner /> : <HiMagnifyingGlass />}
            </Button>
          </GridItem>
        </Grid>
      </>
    );
  }

  function uploadButton() {
    return (
      <>
        <Button position="relative" isDisabled={!islicenseValidated}>
          Upload
          <input
            disabled={!islicenseValidated}
            className="file-upload"
            type="file"
            onChange={(e) => {
              setFile(e.target.files);
            }}
          />
        </Button>{' '}
        {file && file[0].name}
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(createHandler)}>
        <ModalBody>
          <Box className={'ProfileForm'}>
            <div className="transfer-details-content">
              <TableContainer marginBottom={'2rem'} flex={1}>
                <Table>
                  <Tbody>
                    <TableElement label={'License ID'} verticalAlign={'middle'}>
                      {renderInputField('', 'store_license_key')}
                      {errors.store_license_key && (
                        <Text as="sub" color="red">
                          {errors.store_license_key.message}
                        </Text>
                      )}
                    </TableElement>
                    <TableElement label={'License Type'}>
                      {profileData.store_lic_type}
                    </TableElement>
                    <TableElement label={BUSINESS_NAME}>
                      {profileData.name}
                    </TableElement>
                    <TableElement label={'Contact'}>
                      {formatPhoneNumber(profileData?.contact_phone as string)}
                    </TableElement>
                    <TableElement label={'Fax'}>
                      {renderInputField('', 'store_contact_fax')}
                    </TableElement>
                    <TableElement label={'Contact Email'}>
                      {renderInputField('', 'contact_email')}
                    </TableElement>
                  </Tbody>
                </Table>
              </TableContainer>
              <TableContainer marginBottom={'2rem'} flex={1}>
                <Table>
                  <Tbody>
                    <TableElement label={'Sender Email'}>
                      {renderInputField('', 'seller_email')}
                      {errors.seller_email && (
                        <Text as="sub" color="red">
                          {errors.seller_email.message}
                        </Text>
                      )}
                    </TableElement>
                    <TableElement label={'Mailing Address'}>
                      <Box>{renderInputField('', 'store_premise_street')}</Box>
                      <Box>{renderInputField('', 'store_premise_city')}</Box>
                      <Box>{renderInputField('', 'store_premise_state')}</Box>
                      <Box>
                        {renderInputField('', 'store_premise_zip_code')}
                      </Box>
                    </TableElement>
                    <TableElement label={'License Certificate'}>
                      {uploadButton()}
                    </TableElement>
                  </Tbody>
                </Table>
              </TableContainer>
            </div>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" isDisabled={isCreateLoading} type="submit">
            Create Store
          </Button>
        </ModalFooter>
      </form>
      <ToastContainer hideProgressBar />
    </>
  );
};
